import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react'
import { ExportFormat, ExportMethod } from '@dtx-company/flow-codegen/src/code/generated.types'
import { FLOWCODE_API_ENDPOINT } from '@dtx-company/true-common/src/constants/endpoints'
import { RootCodeState } from '@dtx-company/inter-app/src/redux/types'
import { downloadFromURL } from '@app/page/src/utils/download'
import {
  sendErrorNotification,
  sendSuccessNotification
} from '@dtx-company/inter-app/src/utils/notifications'
import { useAuthState } from '@dtx-company/inter-app/src/hooks/useAuthState'
import { useCodeSelector } from '@dtx-company/inter-app/src/hooks/useCodeSelector'
import { useDhProjectId } from './EditTemplatePage.hooks'
import { useLazyPrintStorePreviewImageUrlQuery } from '@dtx-company/inter-app/src/redux/slices/printStoreApiSlice/printStoreQueries'
import { usePrintStoreExportProjectMutation } from '@dtx-company/inter-app/src/redux/slices/printStoreApiSlice/printStoreMutations'
import { useRouter } from 'next/router'
import events from '@dtx-company/inter-app/src/event-tracking/events/printStore'

export interface UseDownloadOrPrintTemplate {
  onDownloadClick: () => void
  onPrintClick: () => void
  downloadIsFetching: boolean
  printIsFetching: boolean
  exportFormat: ExportFormat
  setExportFormat: Dispatch<SetStateAction<ExportFormat>>
}
const iframeId = 'print-template-iframe'

export const useDownloadOrPrintTemplate = (): UseDownloadOrPrintTemplate => {
  const [downloadClicked, setDownloadClicked] = useState(false)
  const [trigger, { isFetching }] = useLazyPrintStorePreviewImageUrlQuery()
  const [exportProjectMutation] = usePrintStoreExportProjectMutation()
  const dhProjectId = useDhProjectId()
  const router = useRouter()
  const [exportFormat, setExportFormat] = useState(ExportFormat.Pdf)

  const { batchId, dhProject } = useCodeSelector((state: RootCodeState) => state.printStore)

  const clearPrintIframe = useCallback((): void => {
    const iframe = document.getElementById(iframeId)
    if (iframe) {
      document.body.removeChild(iframe)
    }
  }, [])

  const onDownloadClick = (): void => {
    setDownloadClicked(true)
    events.PrintTemplates_TemplateEditor_DownloadModal_clicked_download()
    if (!dhProjectId || !batchId) return
    trigger({ dhProjectId, format: exportFormat })
      .then(async ({ isSuccess, data, isError, error }) => {
        if (isSuccess && data.getImageUrl?.imageUrl) {
          const encodedImageUrl = encodeURIComponent(data.getImageUrl?.imageUrl)
          await downloadFromURL(
            `/api/store/templates/proxy?previewImageUrl=${encodedImageUrl}`,
            dhProject?.project_title ?? 'flowcode-template'
          )
          // export project to be called only when trigger is successful
          await exportProjectMutation({
            dhProjectId,
            exportMethod: ExportMethod.Download,
            dhProjectData: JSON.stringify(dhProject),
            flowcodeId: batchId
          })
        }
        if (isError && error) throw new Error(error.message)
      })
      .catch(error => {
        console.error(error, { dhProjectId, batchId })
        sendErrorNotification('There was a problem downloading your Template. Please try again.')
      })
      .finally(() => setDownloadClicked(false))
  }

  const onPrintClick = (): void => {
    events.PrintTemplates_TemplateEditor_DownloadModal_clicked_print()
    clearPrintIframe()

    if (!dhProjectId || !batchId) return
    const iframe = document.createElement('iframe')
    trigger({ dhProjectId })
      .then(async ({ isSuccess, data, isError, error }) => {
        const previewImageUrl = data?.getImageUrl?.imageUrl
        if (isSuccess && previewImageUrl) {
          iframe.style.display = 'none'
          const handlePrint = (): void => {
            iframe.focus()
            iframe.contentWindow?.print()
          }
          iframe.onload = () => {
            setTimeout(handlePrint, 1000)
          }

          const queryParam = encodeURIComponent(previewImageUrl)
          iframe.src = `/api/store/templates/proxy?previewImageUrl=${queryParam}`
          iframe.id = iframeId
          document.body.appendChild(iframe)

          await exportProjectMutation({
            dhProjectId,
            exportMethod: ExportMethod.PrintAtHome,
            dhProjectData: JSON.stringify(dhProject),
            flowcodeId: batchId
          })
        }
        if (isError && error) throw new Error(error.message)
      })
      .catch(error => {
        console.error(error, { dhProjectId, batchId })
        sendErrorNotification('There was a problem printing your Template. Please try again.')
      })
  }

  useEffect(() => {
    router.events.on('routeChangeStart', clearPrintIframe)
    return () => {
      router.events.off('routeChangeStart', clearPrintIframe)
    }
  }, [clearPrintIframe, router.events])

  return {
    onDownloadClick,
    onPrintClick,
    downloadIsFetching: downloadClicked && isFetching,
    printIsFetching: !downloadClicked && isFetching,
    exportFormat,
    setExportFormat
  }
}

export interface UseEmailTemplateReturn {
  onEmailClick: (emails: string) => Promise<void>
  isFetching: boolean
}

export const useEmailTemplate = (): UseEmailTemplateReturn => {
  const dhProjectId = useDhProjectId()
  const { batchId, dhProject } = useCodeSelector((state: RootCodeState) => state.printStore)
  const { jwt, token } = useAuthState()
  const email = jwt?.email

  const body = {
    send_to_email: email,
    dh_project_id: dhProjectId,
    flowcode_id: batchId,
    template_customization: dhProject
  }
  const headers = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...(!!token && { Authorization: `Bearer ${token}` })
  }

  const [isFetching, setIsFetching] = useState(false)

  const emailTemplate = async (emails: string): Promise<void> => {
    body.send_to_email = emails

    try {
      setIsFetching(true)
      const response = await fetch(`${FLOWCODE_API_ENDPOINT}/v2/flowcode/dh-template/email`, {
        method: 'POST',
        body: JSON.stringify(body),
        headers
      })
      if (!response.ok) {
        throw new Error(`Failed to send email: ${response.status} - ${response.statusText}`)
      }
      sendSuccessNotification('Email sent successfully!')
    } catch (err) {
      console.error(err, { body })
      sendErrorNotification('There was a problem emailing your Template. Please try again.')
    } finally {
      setIsFetching(false)
    }
  }

  const onEmailClick = async (emails: string): Promise<void> => {
    events.PrintTemplates_TemplateEditor_EmailModal_clicked_submit(emails)
    if (!dhProjectId || !batchId) return
    emailTemplate(emails)
  }

  return {
    onEmailClick,
    isFetching
  }
}
